import {
  ComponentProps,
  createContext,
  FC,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  ReactPortal,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import styled, { CSSObject } from 'styled-components';
import create from 'zustand';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { DATEPICKER_PORTAL_ATTR } from '@components/datepicker';
import { HorizontalTabs } from '@components/horizontal-tabs';
import { IconButton } from '@components/icon-button';
import { BaseIcon, IconProps } from '@components/icons/base';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconHelp } from '@components/icons/help';
import { RelativeSpinner } from '@components/loading-spinner';
import { HEADLESS_PORTAL_ID } from '@components/menu';
import { useIsModalActive } from '@components/modal/modal';
import { useIsOverlayActive } from '@components/overlay';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Keys } from '@components/types/keyboard';
import { useIsEscapeLayerActive } from '@hooks/use-escape-layer';
import { useKeyPress } from '@hooks/use-keypress';
import { useWindowDimensions } from '@hooks/use-window-dimensions';
import { createCountStore } from '@stores/create-count-store';
import { Color, Font, theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { MakePropertiesOptional, Transient } from '@utils/types';

export type SidebarPosition = 'left' | 'right';

export type SidebarOptions = {
  id?: string;
  extendedId?: string;
  hasBackdrop?: boolean;
  variant?: 'REGULAR' | 'LARGE' | 'CONTENT_HEAVY' | 'REGULAR_EXPANDED';
  isOverrideEnabled?: boolean;
  closeOnClickOutside?: boolean;
  onHide?(): void;
  onBeforeChange?(): boolean | void;
  containerStyle?: CSSObject;
  backgroundColor?: Color | 'gradient';
  zIndex?: number;
  position?: SidebarPosition;
};

export type SidebarComponent = (props: any) => ReactElement;

type SidebarRegistry = {
  options?: SidebarOptions;
  contentPortal: ReactPortal;
  extendedContentComponent?: SidebarComponent;
  extendedContentProps?: Record<string, any>;
};

const sidebarRegistries: Record<SidebarPosition, SidebarRegistry | null> = {
  left: null,
  right: null,
};

// Custom sidebar store with activeId to prevent unnecessary re-renders
type SidebarStore = {
  count: number;
  activeId: string | undefined;
  leftCount: number;
  rightCount: number;
  leftActiveId: string | undefined;
  rightActiveId: string | undefined;
  openStack: Array<SidebarPosition>;
};

const useSidebarStore = create<SidebarStore>(() => ({
  count: 0,
  activeId: undefined,
  leftCount: 0,
  rightCount: 0,
  leftActiveId: undefined,
  rightActiveId: undefined,
  openStack: [],
}));

const sidebarStore = {
  useStore: useSidebarStore,
  increase: () =>
    useSidebarStore.setState((state) => ({
      count: state.count + 1,
      rightCount: state.rightCount + 1,
    })),
  reset: () =>
    useSidebarStore.setState((state) => ({
      count: 0,
      activeId: undefined,
      rightCount: 0,
      rightActiveId: undefined,
      openStack: state.openStack.filter((position) => position !== 'right'),
    })),
  setActiveId: (id: string | undefined) =>
    useSidebarStore.setState({ activeId: id, rightActiveId: id }),
  increaseLeft: () => useSidebarStore.setState((state) => ({ leftCount: state.leftCount + 1 })),
  resetLeft: () =>
    useSidebarStore.setState((state) => ({
      leftCount: 0,
      leftActiveId: undefined,
      openStack: state.openStack.filter((position) => position !== 'left'),
    })),
  setLeftActiveId: (id: string | undefined) => useSidebarStore.setState({ leftActiveId: id }),
  pushToStack: (position: SidebarPosition) =>
    useSidebarStore.setState((state) => {
      const filtered = state.openStack.filter((pos) => pos !== position);
      return { openStack: [...filtered, position] };
    }),
  popFromStack: () =>
    useSidebarStore.setState((state) => {
      if (state.openStack.length === 0) return state;
      return { openStack: state.openStack.slice(0, -1) };
    }),
};

const expandedStore = createCountStore();
const leftExpandedStore = createCountStore();

const handleHideByPosition = (position: SidebarPosition, forceHide: boolean) => {
  const registry = sidebarRegistries[position];
  if (!registry?.options) {
    // No sidebar open at this position - nothing to hide, proceed
    return true;
  }
  const { onBeforeChange, onHide } = registry.options;
  registry.options.isOverrideEnabled = false;
  const shouldContinue = forceHide || (onBeforeChange?.() ?? true);
  if (shouldContinue) {
    sidebarRegistries[position] = null;
    onHide?.();
    if (position === 'left') {
      sidebarStore.resetLeft();
      leftExpandedStore.reset();
    } else {
      sidebarStore.reset();
      expandedStore.reset();
    }
    return true;
  }
  return false;
};

const handleHide = (forceHide: boolean) => handleHideByPosition('right', forceHide);
const handleHideLeft = (forceHide: boolean) => handleHideByPosition('left', forceHide);

export const hideSidebar = (): boolean => handleHide(false);
export const hideRightSidebar = (): boolean => handleHide(false);
export const hideLeftSidebar = (): boolean => handleHideLeft(false);

export const hideSidebarById = (id: string): boolean => {
  const { activeId } = useSidebarStore.getState();
  return activeId === id ? handleHide(false) : false;
};

export const forceHideSidebar = (): boolean => handleHide(true);
export const forceHideRightSidebar = forceHideSidebar;
export const forceHideLeftSidebar = (): boolean => handleHideLeft(true);

const hideExtendedSidebarByPosition = (position: SidebarPosition) => {
  const registry = sidebarRegistries[position];
  if (registry?.extendedContentComponent) {
    registry.extendedContentComponent = undefined;
    registry.extendedContentProps = undefined;
    if (registry.options) {
      registry.options.extendedId = undefined;
    }
    const store = position === 'left' ? leftExpandedStore : expandedStore;
    store.reset();
  }
};

export const hideExtendedSidebar = () => hideExtendedSidebarByPosition('right');
export const hideLeftExtendedSidebar = () => hideExtendedSidebarByPosition('left');

const SIDEBAR_ID = 'sidebar';
export const HEADER_ID = 'sidebar-header';
export const CONTENT_ID = 'sidebar-content';
const OVERRIDE_CONTENT_ID = 'sidebar-override-content';
export const FOOTER_ID = 'sidebar-footer';

const LEFT_SIDEBAR_ID = 'sidebar-left';
const LEFT_HEADER_ID = 'sidebar-left-header';
const LEFT_CONTENT_ID = 'sidebar-left-content';
const LEFT_OVERRIDE_CONTENT_ID = 'sidebar-left-override-content';
const LEFT_FOOTER_ID = 'sidebar-left-footer';

const hiddenStyle = { height: 0, opacity: 0, overflow: 'hidden' };

/**
 * Options are read off a ref rather than memoised: callers pass inline
 * callbacks, and dequal never reports two closures as equal, so a memo would
 * hand back a new identity every render and re-show the sidebar from any
 * effect depending on it.
 */
export const useSidebar = <T extends SidebarComponent>(component: T, options?: SidebarOptions) => {
  const latestOptions = useRef(options);
  latestOptions.current = options;

  return useCallback(
    <K extends ComponentProps<T>>(props?: K, optionsOnShow?: SidebarOptions) => {
      const mergedOptions = { ...latestOptions.current, ...optionsOnShow };
      const position = mergedOptions?.position ?? 'right';
      if (position === 'left') {
        return showLeftSidebar({
          component,
          options: mergedOptions,
          props,
        });
      }
      return showSidebar({
        component,
        options: mergedOptions,
        props,
      });
    },
    [component]
  );
};

export const useSidebarState = () => {
  const { useStore } = sidebarStore;
  const isActive = useStore((s) => s.count);
  const activeId = useStore((s) => s.activeId);

  return { isActive, activeId };
};

export const useLeftSidebarState = () => {
  const { useStore } = sidebarStore;
  const isActive = useStore((s) => s.leftCount);
  const activeId = useStore((s) => s.leftActiveId);

  return { isActive, activeId };
};

export const useRightSidebarState = () => {
  const { useStore } = sidebarStore;
  const isActive = useStore((s) => s.rightCount);
  const activeId = useStore((s) => s.rightActiveId);

  return { isActive, activeId };
};

export const useIsSidebarActive = (id: string) => {
  return useSidebarStore((s) => s.activeId === id);
};

export const useIsLeftSidebarActive = (id: string) => {
  return useSidebarStore((s) => s.leftActiveId === id);
};

export const useIsRightSidebarActive = (id: string) => {
  return useSidebarStore((s) => s.rightActiveId === id);
};

const showSidebarByPosition = <T extends SidebarComponent, K extends ComponentProps<T>>({
  component: SidebarComponent,
  options,
  props,
  position,
}: {
  component: T;
  options?: SidebarOptions;
  props?: K;
  position: SidebarPosition;
}) => {
  const sidebarId = position === 'left' ? LEFT_SIDEBAR_ID : SIDEBAR_ID;
  const sidebarElement = document.getElementById(sidebarId);
  const shouldContinue = sidebarRegistries[position]?.options?.onBeforeChange?.() ?? true;
  hideExtendedSidebarByPosition(position);
  if (!sidebarElement || !shouldContinue) {
    return;
  }
  // To re-mount the sidebar when it is reopened, so we don't re-use old state
  const ts = Date.now().valueOf();
  // Cast to FC<any> to work around complex generic constraint issues with spread props
  const Component = SidebarComponent as React.FC<Record<string, unknown>>;
  const contentPortal = createPortal(<Component key={ts} {...props} />, sidebarElement);
  sidebarRegistries[position] = {
    options: { ...options, ...props, position },
    contentPortal,
  };
  if (position === 'left') {
    sidebarStore.setLeftActiveId(options?.id);
    sidebarStore.increaseLeft();
  } else {
    sidebarStore.setActiveId(options?.id);
    sidebarStore.increase();
  }
  sidebarStore.pushToStack(position);
};

export const showSidebar = <T extends SidebarComponent, K extends ComponentProps<T>>({
  component,
  options,
  props,
}: {
  component: T;
  options?: SidebarOptions;
  props?: K;
}) => showSidebarByPosition({ component, options, props, position: 'right' });

export const showRightSidebar = showSidebar;

export const showLeftSidebar = <T extends SidebarComponent, K extends ComponentProps<T>>({
  component,
  options,
  props,
}: {
  component: T;
  options?: SidebarOptions;
  props?: K;
}) => showSidebarByPosition({ component, options, props, position: 'left' });

export const useSidebarEventHandlers = (position: SidebarPosition = 'right') => {
  const setOnBeforeChange = (onBeforeChange: SidebarOptions['onBeforeChange']) => {
    const registry = sidebarRegistries[position];
    if (registry?.options) {
      registry.options = { ...registry.options, onBeforeChange };
    }
  };

  const setOnHide = (onHide: SidebarOptions['onHide']) => {
    const registry = sidebarRegistries[position];
    if (registry?.options) {
      registry.options = { ...registry.options, onHide };
    }
  };

  return { setOnBeforeChange, setOnHide };
};

export const useLeftSidebarEventHandlers = () => useSidebarEventHandlers('left');
export const useRightSidebarEventHandlers = () => useSidebarEventHandlers('right');

const TRANSITION_DURATION = 0.3;
const BACKDROP_OPACITY = 0.5;

// Exported so consumers that render above the sidebar (e.g. a floating panel
// with no backdrop) can derive their own z-index from it instead of guessing
// or duplicating the literal.
export const SIDEBAR_DEFAULT_Z_INDEX = 9990;

const LARGE_BREAKPOINT = 768;
const LARGE_MAX_WIDTH = 696;
const REGULAR_MAX_WIDTH = 472;

const useSidebarWidth = (variant: SidebarOptions['variant']) => {
  const { width } = useWindowDimensions();
  const dynamicWidth =
    variant !== 'REGULAR'
      ? width > LARGE_BREAKPOINT
        ? LARGE_MAX_WIDTH
        : REGULAR_MAX_WIDTH
      : REGULAR_MAX_WIDTH;
  const validWidth = Math.min(width, dynamicWidth);
  return pxToRem(validWidth);
};

export const Sidebar: React.FC<React.HTMLAttributes<HTMLDivElement>> & {
  Provider: typeof SidebarProvider;
  SimpleHeader: typeof SimpleHeader;
  HeaderTitle: typeof HeaderTitle;
  Header: typeof SidebarHeader;
  Content: typeof SidebarContent;
  Footer: typeof SidebarFooter;
  Separator: typeof ContentSeparator;
  Spinner: typeof SidebarSpinner;
  SplitContainer: typeof SidebarSplitContainer;
} = (props) => <div {...props} />;

export const setSidebarVariant = (
  variant: SidebarOptions['variant'],
  position: SidebarPosition = 'right'
) => {
  const registry = sidebarRegistries[position];
  if (registry?.options) {
    registry.options.variant = variant;
    if (position === 'left') {
      sidebarStore.increaseLeft();
    } else {
      sidebarStore.increase();
    }
  }
};

export const setLeftSidebarVariant = (variant: SidebarOptions['variant']) =>
  setSidebarVariant(variant, 'left');
export const setRightSidebarVariant = (variant: SidebarOptions['variant']) =>
  setSidebarVariant(variant, 'right');

const VariantContext = createContext<SidebarOptions['variant']>('REGULAR');
const OverrideContext = createContext<{
  isEnabled: Boolean;
  setIsEnabled: (isEnabled: boolean) => void;
}>({ isEnabled: false, setIsEnabled: noop });

export const useSidebarOverrideContext = () => {
  const overrideContext = useContext(OverrideContext);

  return overrideContext;
};

type ShowExpandedSidebarProps = {
  component: SidebarComponent;
  props?: Record<string, any>;
  extendedId?: SidebarOptions['extendedId'];
  position?: SidebarPosition;
};

export const showExtendedSidebar = ({
  component,
  props,
  extendedId,
  position = 'right',
}: ShowExpandedSidebarProps) => {
  const registry = sidebarRegistries[position];
  const store = position === 'left' ? leftExpandedStore : expandedStore;
  if (registry) {
    sidebarRegistries[position] = {
      ...registry,
      extendedContentComponent: component,
      extendedContentProps: props,
      options: { ...registry.options, extendedId },
    };
    store.increase();
  }
};

export const showLeftExtendedSidebar = (props: Omit<ShowExpandedSidebarProps, 'position'>) =>
  showExtendedSidebar({ ...props, position: 'left' });
export const showRightExtendedSidebar = (props: Omit<ShowExpandedSidebarProps, 'position'>) =>
  showExtendedSidebar({ ...props, position: 'right' });

export const useExtendedSidebarData = (position: SidebarPosition = 'right') => {
  const store = position === 'left' ? leftExpandedStore : expandedStore;
  const isExtended = Boolean(store.useStore((s) => s.count));
  const extendedId = sidebarRegistries[position]?.options?.extendedId;
  return { isExtended, extendedId };
};

export const useLeftExtendedSidebarData = () => useExtendedSidebarData('left');
export const useRightExtendedSidebarData = () => useExtendedSidebarData('right');

const handleEscapeKey = () => {
  const stack = useSidebarStore.getState().openStack;
  if (stack.length === 0) return;
  const mostRecent = stack[stack.length - 1];
  if (mostRecent === 'left') {
    hideLeftSidebar();
  } else {
    hideRightSidebar();
  }
};

type SidebarProviderPanelProps = {
  position: SidebarPosition;
};

const SidebarProviderPanel = ({ position }: SidebarProviderPanelProps) => {
  const isActive = sidebarStore.useStore((s) => (position === 'left' ? s.leftCount : s.rightCount));
  const { isExtended } = useExtendedSidebarData(position);
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const registry = sidebarRegistries[position];
  const { options, contentPortal } = registry ?? {};
  const ExtendedContent = registry?.extendedContentComponent;
  const extendedContentProps = registry?.extendedContentProps;

  const {
    hasBackdrop,
    variant,
    closeOnClickOutside = false,
    isOverrideEnabled = false,
    backgroundColor,
    zIndex,
  } = options ?? {};
  const baseZIndex = zIndex ?? SIDEBAR_DEFAULT_Z_INDEX;

  const setIsOverrideEnabled = useCallback(
    (isEnabled: boolean) => {
      const registry = sidebarRegistries[position];
      if (registry?.options) {
        registry.options.isOverrideEnabled = isEnabled;
        if (position === 'left') {
          sidebarStore.increaseLeft();
        } else {
          sidebarStore.increase();
        }
      }
    },
    [position]
  );

  const width = useSidebarWidth(variant);

  const hideSidebarFn = position === 'left' ? forceHideLeftSidebar : forceHideRightSidebar;
  const showHideSidebarFn = position === 'left' ? hideLeftSidebar : hideRightSidebar;

  useEffect(() => {
    hideSidebarFn();
  }, [location, hideSidebarFn]);

  useEffect(() => {
    if (position !== 'right' || !closeOnClickOutside || !isActive) return;

    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;

      const isClickInside = (selectors: string[]) =>
        selectors.some((selector) => clickedElement.closest(selector));

      // Allow clicks inside popups like Menu or DatePicker, as they are rendered outside the sidebar and would otherwise close it.
      const allowedTargets = [`#${HEADLESS_PORTAL_ID}`, `[${DATEPICKER_PORTAL_ATTR}]`];

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(clickedElement) &&
        !isClickInside(allowedTargets)
      ) {
        hideRightSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, closeOnClickOutside, position]);

  const overrideHideStyle = isOverrideEnabled ? hiddenStyle : {};

  const sidebarId = position === 'left' ? LEFT_SIDEBAR_ID : SIDEBAR_ID;
  const headerId = position === 'left' ? LEFT_HEADER_ID : HEADER_ID;
  const contentId = position === 'left' ? LEFT_CONTENT_ID : CONTENT_ID;
  const overrideContentId = position === 'left' ? LEFT_OVERRIDE_CONTENT_ID : OVERRIDE_CONTENT_ID;
  const footerId = position === 'left' ? LEFT_FOOTER_ID : FOOTER_ID;

  return (
    <StyledSidebar
      id={sidebarId}
      ref={sidebarRef}
      $backgroundColor={backgroundColor as Color | 'gradient' | undefined}
      $zIndex={baseZIndex}
      $position={position}
      $isActive={Boolean(isActive)}
      className={backgroundColor === 'gradient' ? 'dark' : undefined}
    >
      <SidebarPositionContext.Provider value={position}>
        <OverrideContext.Provider
          value={{ isEnabled: isOverrideEnabled, setIsEnabled: setIsOverrideEnabled }}
        >
          {contentPortal}
          <AnimatePresence>
            {isActive && hasBackdrop && position === 'right' && (
              <Backdrop
                initial={{ opacity: 0 }}
                animate={{ opacity: BACKDROP_OPACITY }}
                transition={{ duration: TRANSITION_DURATION }}
                exit={{ opacity: 0 }}
                onClick={showHideSidebarFn}
              />
            )}
          </AnimatePresence>
          <AnimationContainer
            initial={false}
            transition={{ stiffness: 100, duration: TRANSITION_DURATION }}
            animate={{ width: isActive ? width : 0 }}
            $zIndex={baseZIndex}
            $position={position}
          >
            <SidebarExtendedContentContainer
              data-testid="extended-sidebar-panel"
              $isExtended={isExtended}
              $sidebarWidth={width}
              $position={position}
            >
              {ExtendedContent !== undefined ? (
                <ExtendedContent {...(extendedContentProps ?? {})} />
              ) : null}
            </SidebarExtendedContentContainer>
            <VariantContext.Provider value={variant}>
              <SidebarContainer
                $width={width}
                $containerStyle={options?.containerStyle}
                $backgroundColor={backgroundColor}
                $position={position}
              >
                <div id={headerId} />
                <ContentContainer>
                  <ContentWrapper variant={variant}>
                    <StyledDiv id={contentId} style={overrideHideStyle} />
                    <StyledDiv id={overrideContentId} $hideOverride={!isOverrideEnabled} />
                  </ContentWrapper>
                </ContentContainer>
                <div id={footerId} style={overrideHideStyle} />
              </SidebarContainer>
            </VariantContext.Provider>
          </AnimationContainer>
        </OverrideContext.Provider>
      </SidebarPositionContext.Provider>
    </StyledSidebar>
  );
};

const SidebarProvider = () => {
  const isModalActive = useIsModalActive();
  const isOverlayActive = useIsOverlayActive();
  const isEscapeLayerActive = useIsEscapeLayerActive();
  const openStack = sidebarStore.useStore((s) => s.openStack);

  useKeyPress({
    key: Keys.Escape,
    enabled: !isModalActive && !isOverlayActive && !isEscapeLayerActive && openStack.length > 0,
    handler: handleEscapeKey,
  });

  return (
    <>
      <SidebarProviderPanel position="left" />
      <SidebarProviderPanel position="right" />
    </>
  );
};

const StyledDiv = styled.div(({ $hideOverride }: { $hideOverride?: boolean }) => ({
  transition: 'opacity 0.3s ease-in-out',
  '&:after': {
    display: 'block',
    content: "''",
    width: '100%',
    height: $hideOverride ? 0 : pxToRem(40),
  },
}));

type StyledSidebarProps = Transient<Pick<SidebarOptions, 'backgroundColor' | 'zIndex'>> & {
  $position: SidebarPosition;
  $isActive: boolean;
};

const StyledSidebar = styled.div<StyledSidebarProps>(
  ({ $backgroundColor, $zIndex, $position, $isActive }) => ({
    position: 'fixed',
    top: 0,
    right: $position === 'right' ? 0 : undefined,
    left: $position === 'left' ? 0 : undefined,
    zIndex: $zIndex ?? SIDEBAR_DEFAULT_Z_INDEX,
    height: '100vh',
    boxShadow: $isActive ? theme.shadow.fancy : 'none',
    pointerEvents: $isActive ? 'auto' : 'none',
    ...($backgroundColor && {
      '&': {
        backgroundColor: $backgroundColor !== 'gradient' ? $backgroundColor : undefined,
        ...($backgroundColor === 'gradient' && {
          backgroundColor: theme.color.purple400,
          backgroundImage:
            $position === 'right'
              ? `radial-gradient(ellipse 120% 100% at 100% 50%, ${theme.color.violet100} -64%, ${theme.color.tealTrans10} 70%)`
              : `radial-gradient(ellipse 120% 100% at 0% 50%, ${theme.color.violet100} -64%, ${theme.color.tealTrans10} 70%)`,
          color: 'white',
        }),
      },
      [`aside, .${HEADER_CONTAINER_CLASSNAME}, .${FOOTER_CONTAINER_CLASSNAME}`]: {
        backgroundColor: 'transparent',
      },
    }),
  })
);

type AnimationContainerProps = Transient<Pick<SidebarOptions, 'zIndex'>> & {
  $position: SidebarPosition;
};

const AnimationContainer = styled(motion.aside)<AnimationContainerProps>(
  ({ $zIndex, $position }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    // ensure the animated container sits above the backdrop
    zIndex: ($zIndex ?? SIDEBAR_DEFAULT_Z_INDEX) + 1,
    backgroundColor: theme.color.backgroundLightDark,
    direction: $position === 'left' ? 'rtl' : undefined,
  })
);

type SidebarContainerProps = {
  $width: string;
  $containerStyle?: CSSObject;
  $backgroundColor: SidebarOptions['backgroundColor'];
  $position: SidebarPosition;
};

const SidebarContainer = styled.div<SidebarContainerProps>(
  ({ $width, $containerStyle, $backgroundColor, $position }) => ({
    minWidth: $width,
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    overflow: 'hidden',
    ...($position === 'right'
      ? { borderLeft: `${pxToRem(1)} solid ${theme.color.border10}` }
      : { borderRight: `${pxToRem(1)} solid ${theme.color.border10}` }),
    zIndex: 1,
    backgroundColor: !$backgroundColor ? theme.color.backgroundLightDark : 'transparent',
    direction: 'ltr',
    ...$containerStyle,
  })
);

const Backdrop = styled(motion.div)({
  position: 'fixed',
  backgroundColor: theme.color.backdropOverlay,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: 'hidden',
  zIndex: -1,
});

const ContentContainer = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'auto',
  minHeight: 0,
  flexGrow: 1,
  overflowX: 'hidden',
});

const ContentWrapper = styled.div<Pick<SidebarOptions, 'variant'>>(({ variant }) => ({
  flex: 1,
  margin:
    variant === 'REGULAR' || variant === 'CONTENT_HEAVY'
      ? pxToRem(24)
      : `${pxToRem(24)} ${pxToRem(56)}`,
  marginBottom: pxToRem(48),
  maxWidth: variant === 'CONTENT_HEAVY' ? pxToRem(648) : pxToRem(424),
}));

const headerFooterPadding = `${pxToRem(16)} ${pxToRem(24)}`;

type HeaderProps = { hasTabs?: boolean };

const HEADER_CONTAINER_CLASSNAME = 'header-container';

const Header = styled.div.attrs({
  className: HEADER_CONTAINER_CLASSNAME,
})<HeaderProps>(({ hasTabs }) => ({
  padding: headerFooterPadding,
  borderBottom: hasTabs ? undefined : `${pxToRem(1)} solid`,
  borderColor: theme.color.border10,
  marginBottom: hasTabs ? `-${pxToRem(16)}` : undefined,
  backgroundColor: theme.color.backgroundLightDark,
  '@media (max-width: 768px)': {
    height: pxToRem(72),
  },
}));

const FOOTER_CONTAINER_CLASSNAME = 'footer-container';

const Footer = styled.div.attrs({
  className: FOOTER_CONTAINER_CLASSNAME,
})({
  padding: headerFooterPadding,
  borderTop: `${pxToRem(1)} solid`,
  borderColor: theme.color.border10,
  backgroundColor: theme.color.backgroundLightDark,
  '@media (max-width: 768px)': {
    height: pxToRem(72),
  },
});

const SidebarHeader: React.FC<HeaderProps> = (props) => {
  const position = useSidebarPosition();
  const headerId = position === 'left' ? LEFT_HEADER_ID : HEADER_ID;
  const headerElement = document.getElementById(headerId);
  if (!headerElement) {
    return null;
  }

  return createPortal(<Header {...props} />, headerElement);
};

const SidebarContent: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  const position = useSidebarPosition();
  const contentId = position === 'left' ? LEFT_CONTENT_ID : CONTENT_ID;
  const contentElement = document.getElementById(contentId);
  const isReadOnly = useSidebarReadOnlyContext();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isReadOnly) {
      contentRef.current?.setAttribute('inert', '');
    } else {
      contentRef.current?.removeAttribute('inert');
    }
  }, [isReadOnly]);

  if (!contentElement) {
    return null;
  }

  return createPortal(
    <>
      <div ref={contentRef} style={{ position: 'relative' }} {...rest}>
        {isReadOnly && <Overlay />}
        {children}
      </div>
    </>,
    contentElement
  );
};

const Overlay = styled.div({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  zIndex: 1,
  height: '100%',
  width: '100%',
});

export const SidebarReadOnlyContext = createContext(false);

export const useSidebarReadOnlyContext = () => useContext(SidebarReadOnlyContext);

const SidebarPositionContext = createContext<SidebarPosition>('right');

const useSidebarPosition = () => useContext(SidebarPositionContext);

const SidebarOverrideContent: React.FC = (props) => {
  const position = useSidebarPosition();
  const overrideContentId = position === 'left' ? LEFT_OVERRIDE_CONTENT_ID : OVERRIDE_CONTENT_ID;
  const overrideContentElement = document.getElementById(overrideContentId);
  if (!overrideContentElement) {
    return null;
  }

  return createPortal(
    <Slide key="info" offset={-30}>
      <div {...props} />
    </Slide>,
    overrideContentElement
  );
};

const SidebarFooter: React.FC = (props) => {
  const position = useSidebarPosition();
  const footerId = position === 'left' ? LEFT_FOOTER_ID : FOOTER_ID;
  const footerElement = document.getElementById(footerId);
  if (!footerElement) {
    return null;
  }

  return createPortal(<Footer {...props} />, footerElement);
};

type HeaderTabsProps = {
  tabs: Array<{
    label: string;
    content: ReactNode;
    tabCount?: number;
  }>;
  defaultIndex?: number;
  onTabChange?: (index: number) => void;
};

const TabsWrapper = styled.div({
  margin: `auto -${pxToRem(24)}`,
});

const HeaderTabs = ({ tabs, defaultIndex = 0, onTabChange }: HeaderTabsProps) => {
  const parsedTabs = tabs.map((tab, index) => ({
    label: tab.label,
    content: <Sidebar.Content>{tab.content}</Sidebar.Content>,
    index,
    tabCount: tab.tabCount,
  }));

  const { isEnabled } = useSidebarOverrideContext();

  const overrideHideStyle = isEnabled ? hiddenStyle : {};

  return (
    <TabsWrapper style={overrideHideStyle}>
      <HorizontalTabs items={parsedTabs} defaultIndex={defaultIndex} onTabChange={onTabChange} />
    </TabsWrapper>
  );
};

type SimpleIconHeaderProps = {
  title: string;
  subtitle?: string;
  subtitleFont?: Font;
  subtitleLocation?: 'nextTo' | 'below';
  icon: typeof BaseIcon;
  color?: Color;
  iconColor?: Color;
};

export const SimpleIconHeader = ({
  title,
  subtitle,
  iconColor,
  color,
  icon: Icon,
  subtitleFont,
  subtitleLocation,
}: SimpleIconHeaderProps) => (
  <Stack space={2} orientation="horizontal" align="center">
    <Icon color={iconColor ?? color ?? 'buttonIconPositive'} size={24} />
    <HeaderTitle
      color={color}
      title={title}
      subtitle={subtitle}
      subtitleFont={subtitleFont}
      subtitleLocation={subtitleLocation}
    />
  </Stack>
);

export const InfoButton: React.FC = (props) => {
  const { isEnabled, setIsEnabled } = useSidebarOverrideContext();

  return (
    <>
      <IconButton
        variant="ghost-purple"
        icon={<IconHelp color={isEnabled ? 'elementBlue100' : undefined} />}
        onClick={() => setIsEnabled(!isEnabled)}
      />
      {isEnabled && <SidebarOverrideContent {...props} />}
    </>
  );
};

type UseInfoButtonProps = {
  renderComponent: ReactNode;
};

export const useInfoButton = ({ renderComponent }: UseInfoButtonProps) => {
  const { setIsEnabled } = useSidebarOverrideContext();

  return { InfoButton: () => <InfoButton>{renderComponent}</InfoButton>, setIsEnabled };
};

type CloseButtonProps = { onClose?(): void };

export const CloseButton = ({ onClose }: CloseButtonProps) => {
  const position = useSidebarPosition();
  const defaultOnClose = position === 'left' ? hideLeftSidebar : hideSidebar;

  return (
    <Button
      iconLeft={<IconCloseRegular />}
      variant="ghost"
      onClick={onClose ?? defaultOnClose}
      dataCy="close-sidebar"
      dataTestid="close-sidebar"
    />
  );
};

export type SimpleHeaderProps = CloseButtonProps &
  MakePropertiesOptional<SimpleIconHeaderProps, 'icon'> & {
    subtitle?: string;
    tabs?: HeaderTabsProps['tabs'];
    extraHeader?: ReactElement;
    hideExtraHeaderSeparator?: boolean;
    defaultIndex?: number;
    customIcon?: ReactElement<IconProps>;
    hideCloseButton?: boolean;
    onTabChange?: HeaderTabsProps['onTabChange'];
  };

const SimpleHeader: FC<SimpleHeaderProps> = ({
  title,
  subtitle,
  subtitleFont,
  subtitleLocation,
  icon,
  tabs,
  extraHeader,
  hideExtraHeaderSeparator = false,
  children,
  onClose,
  defaultIndex,
  customIcon,
  hideCloseButton = false,
  onTabChange,
  ...rest
}) => (
  <SidebarHeader hasTabs={Boolean(tabs)} {...rest}>
    <Stack space={4}>
      <Stack space={2} orientation="horizontal" align="center" justify="space-between">
        {customIcon ? (
          <Stack space={2} orientation="horizontal" align="center">
            {customIcon}
            <HeaderTitle
              title={title}
              subtitle={subtitle}
              subtitleFont={subtitleFont}
              subtitleLocation={subtitleLocation}
            />
          </Stack>
        ) : icon ? (
          <SimpleIconHeader
            icon={icon}
            title={title}
            subtitle={subtitle}
            subtitleFont={subtitleFont}
            subtitleLocation={subtitleLocation}
            {...rest}
          />
        ) : (
          <HeaderTitle
            title={title}
            subtitle={subtitle}
            subtitleFont={subtitleFont}
            subtitleLocation={subtitleLocation}
          />
        )}
        <Stack space={2} orientation="horizontal" align="center">
          {children}
          {!hideCloseButton && <CloseButton onClose={onClose} />}
        </Stack>
      </Stack>
      {extraHeader && (
        <>
          {!hideExtraHeaderSeparator && <ContentSeparator />}
          {extraHeader}
        </>
      )}
      {tabs && <HeaderTabs tabs={tabs} defaultIndex={defaultIndex} onTabChange={onTabChange} />}
    </Stack>
  </SidebarHeader>
);

const HeaderTitle = ({
  title,
  subtitle,
  subtitleFont = 'bold18',
  subtitleLocation = 'nextTo',
  color,
}: Pick<
  SimpleHeaderProps,
  'title' | 'subtitle' | 'subtitleFont' | 'subtitleLocation' | 'color'
>) => (
  <Stack
    space={subtitleLocation === 'nextTo' ? 1 : 0}
    orientation={subtitleLocation === 'nextTo' ? 'horizontal' : 'vertical'}
  >
    <Text color={color} text={title} font="bold18" />
    {subtitle && <Text color={'textHelper'} text={subtitle} font={subtitleFont} />}
  </Stack>
);

const SeparatorContainer = styled.div({
  overflow: 'hidden',
  marginLeft: -LARGE_MAX_WIDTH,
  marginRight: -LARGE_MAX_WIDTH,
});

const Separator = styled.div({
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
});

const SpinnerContainer = styled.div({
  position: 'absolute',
  left: 0,
  top: '30%',
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ContentSeparator = () => (
  <SeparatorContainer>
    <Separator />
  </SeparatorContainer>
);

const SidebarSpinner = () => {
  return (
    <SpinnerContainer>
      <RelativeSpinner />
    </SpinnerContainer>
  );
};

const StyledSplitContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  '>div:nth-child(2)': {
    width: pxToRem(424),
  },
});

type SidebarSplitContainerProps = {
  label: string;
};

const SidebarSplitContainer: React.FC<SidebarSplitContainerProps> = ({ label, children }) => {
  return (
    <StyledSplitContainer>
      <Text font="semibold14" text={label} />
      {children}
    </StyledSplitContainer>
  );
};

type SidebarExtendedContentContainerProps = {
  $isExtended: boolean;
  $sidebarWidth: string;
  $position: SidebarPosition;
};

const SidebarExtendedContentContainer = styled.div<SidebarExtendedContentContainerProps>(
  ({ $isExtended, $sidebarWidth, $position }) => ({
    position: 'fixed',
    top: 0,
    height: '100%',
    width: $sidebarWidth,
    display: 'flex',
    flexDirection: 'column',
    ...($position === 'right'
      ? { right: $isExtended ? $sidebarWidth : `-${$sidebarWidth}` }
      : { left: $isExtended ? $sidebarWidth : `-${$sidebarWidth}` }),
    transition: 'all 0.3s ease-in-out',
    flexShrink: 0,
    flexGrow: 1,
    backgroundColor: theme.color.backgroundLightDark,
    boxShadow: $isExtended ? theme.shadow.fancy : 'none',
    clipPath:
      $position === 'right' ? `inset(0 0 0 ${pxToRem(-32)})` : `inset(0 ${pxToRem(-32)} 0 0)`,
    overflow: 'hidden',
    direction: 'ltr',
    // Prevents shadow glitching during animation on some browsers
    willChange: 'transform',
    transform: 'translateZ(0)',
  })
);

Sidebar.Provider = SidebarProvider;
Sidebar.SimpleHeader = SimpleHeader;
Sidebar.HeaderTitle = HeaderTitle;
Sidebar.Header = SidebarHeader;
Sidebar.Content = SidebarContent;
Sidebar.Footer = SidebarFooter;
Sidebar.Separator = ContentSeparator;
Sidebar.Spinner = SidebarSpinner;
Sidebar.SplitContainer = SidebarSplitContainer;
